// ── A/R period comparison and A/R roll (step-down) — Borrower Dashboard, A/R tab ──
//
// ArPeriodCard   MoM / QoQ / YoY on every A/R line, on the last value or the
//                period average. Pure arithmetic on the certificate history
//                (src/shared/arPeriods.js), so it works for every borrower.
// ArRollCard     where each aging bucket's dollars went the next month — paid,
//                stayed, or rolled older — following the same invoices
//                (api/getArRoll). Invoice-level borrowers only; for the rest it
//                says why it is blank.
//
// Both sit on the Borrower Dashboard (as of the latest certificate) and the
// certificate page (as of that certificate: the page passes only the history up
// to it, and asOf to the roll).
import { useState, useEffect, useMemo } from "react";
import { B } from "../../shared/theme";
import { fmt, fmtDate } from "../../shared/format";
import { Card, Td, TdNum } from "../../shared/ui";
import { useApp } from "../../shared/appContext";
import { arPeriodCompare } from "../../shared/arPeriods";
import { TH_WRAP } from "./trendCharts";

const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em" };
const th = { fontSize: 12, fontWeight: 600, color: B.cyanDark, background: "#ffffff", ...TH_WRAP, borderBottom: "2px solid " + B.cyan };
const sub = { fontSize: 11, color: "var(--color-text-secondary)" };
const BAD = "#993C1D", GOOD = "#0F6E56";

const Seg = ({ value, options, onChange }) => (
  <div style={{ display: "inline-flex", border: "0.5px solid var(--color-border-secondary)", borderRadius: 6, overflow: "hidden" }}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        style={{ fontSize: 11, padding: "3px 10px", border: "none", cursor: "pointer",
                 background: value === v ? B.cyan : "transparent", color: value === v ? "#fff" : "var(--color-text-secondary)" }}>{l}</button>
    ))}
  </div>
);

// ── MoM / QoQ / YoY ─────────────────────────────────────────────────────────
const valStr = (m, v) => (v == null ? "—" : m.kind === "pct" ? (v * 100).toFixed(1) + "%" : fmt(v));
const chgStr = (m, c) => (c == null ? "—" : m.kind === "pct"
  ? (c >= 0 ? "+" : "") + (c * 100).toFixed(1) + " pts"
  : (c >= 0 ? "+" : "") + (c * 100).toFixed(1) + "%");
const chgColor = (m, c) => {
  if (c == null || !m.worse || Math.abs(c) < 1e-9) return "inherit";
  const up = c > 0;
  return (m.worse === "up") === up ? BAD : GOOD;
};

export const ArPeriodCard = ({ hist }) => {
  const [basis, setBasis] = useState("last");
  const res = useMemo(() => arPeriodCompare(hist, basis), [hist, basis]);
  if (!res.rows.length || !res.rows.some(r => r.cur != null)) return null;
  const L = res.labels;
  const head = basis === "last"
    ? { cur: "Latest " + fmtDate(res.asof), mom: L.mom ? fmtDate(L.mom) : "Prior month", qoq: L.qoq ? fmtDate(L.qoq) : "Prior quarter", yoy: L.yoy ? fmtDate(L.yoy) : "Prior year" }
    : { cur: "This month avg", mom: "Prior month avg", qoq: "Prior quarter avg", yoy: "Same months last year avg" };
  const cell = (m, c) => (
    <>
      <TdNum style={{ color: "var(--color-text-secondary)" }}
        title={c.now != null && c.prior != null ? `${valStr(m, c.prior)} → ${valStr(m, c.now)}` : ""}>{valStr(m, c.prior)}</TdNum>
      <TdNum style={{ fontWeight: 600, color: chgColor(m, c.chg), borderRight: "0.5px solid var(--color-border-tertiary)" }}>{chgStr(m, c.chg)}</TdNum>
    </>
  );
  return (
    <Card style={{ marginBottom: 16, overflowX: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 12px 8px", flexWrap: "wrap" }}>
        <span style={cardTitle}>A/R period comparison</span>
        <span style={sub}>MoM · QoQ · YoY</span>
        <div style={{ marginLeft: "auto" }}>
          <Seg value={basis} onChange={setBasis} options={[["last", "Last value"], ["avg", "Average"]]} />
        </div>
      </div>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: "left" }} rowSpan={2}>Measure</th>
            <th style={{ ...th, textAlign: "right" }} rowSpan={2}>{head.cur}</th>
            <th style={{ ...th, textAlign: "center" }} colSpan={2}>Month over month</th>
            <th style={{ ...th, textAlign: "center" }} colSpan={2}>Quarter over quarter</th>
            <th style={{ ...th, textAlign: "center" }} colSpan={2}>Year over year</th>
          </tr>
          <tr>
            {[head.mom, head.qoq, head.yoy].map((h, i) => [
              <th key={"p" + i} style={{ ...th, textAlign: "right", fontWeight: 400 }}>{h}</th>,
              <th key={"c" + i} style={{ ...th, textAlign: "right" }}>Change</th>,
            ])}
          </tr>
        </thead>
        <tbody>
          {res.rows.map(m => (
            <tr key={m.key} style={m.key === "b0" ? { borderTop: "1px solid var(--color-border-tertiary)" } : null}>
              <Td style={{ paddingLeft: m.indent ? 22 : undefined, fontWeight: m.indent ? 400 : 500,
                           color: m.indent ? "var(--color-text-secondary)" : "inherit" }}>{m.label}</Td>
              <TdNum style={{ fontWeight: 600 }}>{valStr(m, m.cur)}</TdNum>
              {cell(m, m.mom)}{cell(m, m.qoq)}{cell(m, m.yoy)}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ ...sub, padding: "8px 12px 12px", lineHeight: 1.5 }}>
        {basis === "last"
          ? "The latest certificate against the last certificate of the previous month, the previous quarter, and the same month a year earlier."
          : "The average of every certificate in the period: this month so far vs the previous month, this quarter so far vs the previous quarter, this year to date vs the same months last year. Hover a prior figure for the two values compared."}
        {" "}Aging buckets by invoice date; % over 90 days moves in points. Red is the bad direction for that line, green the good one.
      </div>
    </Card>
  );
};

// ── Roll / step-down ────────────────────────────────────────────────────────
const pct = (a, b) => (b > 0 ? (a / b * 100).toFixed(1) + "%" : "—");

export const ArRollCard = ({ accountid, asOf }) => {
  const { api } = useApp();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState(0);
  useEffect(() => {
    let live = true;
    setData(null); setErr(null); setSel(0);
    if (!accountid) return;
    api.get("getArRoll", asOf ? { accountid, months: 12, asof: asOf } : { accountid, months: 12 })
      .then(d => { if (live) setData(d); })
      .catch(e => { if (live) setErr(e.message); });
    return () => { live = false; };
    // eslint-disable-next-line
  }, [accountid, asOf]);

  if (err) return <Card style={{ padding: 16, marginBottom: 16, ...sub }}>A/R roll could not be loaded: {err}</Card>;
  if (!data) return null;
  const { pairs = [], buckets = [], labels = {} } = data;
  if (!pairs.length) return null;
  const comp = pairs.filter(p => p.comparable);

  const title = (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 12px 8px", flexWrap: "wrap" }}>
      <span style={cardTitle}>A/R roll (step-down)</span>
      <span style={sub}>where each bucket's dollars went the next month, following the same invoices</span>
    </div>
  );

  if (!comp.length) return (
    <Card style={{ marginBottom: 16 }}>
      {title}
      <div style={{ ...sub, padding: "0 12px 14px", fontSize: 12, lineHeight: 1.5 }}>
        Not available for this borrower. {pairs[0].reason || ""} The roll needs invoice numbers on the A/R aging, on two
        certificates a month apart, to follow the same invoices from one to the next. A summary aging (one row per
        customer) still shows on every other A/R card.
      </div>
    </Card>
  );

  // Average across the comparable months, dollar-weighted: total rolled out of
  // a bucket over total that bucket held.
  const avg = {};
  buckets.forEach(b => {
    const t = comp.reduce((a, p) => {
      const r = p.rows[b];
      a.prior += r.prior; a.rolled += r.rolled; a.cleared += r.cleared; a.stayed += r.stayed; return a;
    }, { prior: 0, rolled: 0, cleared: 0, stayed: 0 });
    avg[b] = t;
  });
  const p = pairs[Math.min(sel, pairs.length - 1)];
  const shown = p.comparable ? p : comp[0];
  const rollCols = buckets.slice(0, -1);   // 120+ cannot roll older

  return (
    <Card style={{ marginBottom: 16, overflowX: "auto" }}>
      {title}

      {/* Roll rates by month — click a month for its full matrix below */}
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
        <thead><tr>
          <th style={{ ...th, textAlign: "left" }}>Month</th>
          {rollCols.map(b => <th key={b} style={{ ...th, textAlign: "right" }}>{labels[b]} rolled older</th>)}
          <th style={{ ...th, textAlign: "right" }}>Paid (all buckets)</th>
          <th style={{ ...th, textAlign: "right" }}>New billings</th>
        </tr></thead>
        <tbody>
          <tr style={{ background: "#FDF0D5" }}>
            <Td style={{ fontWeight: 700 }}>Average ({comp.length} mo)</Td>
            {rollCols.map(b => <TdNum key={b} style={{ fontWeight: 700 }}>{pct(avg[b].rolled, avg[b].prior)}</TdNum>)}
            <TdNum style={{ fontWeight: 700 }}>{pct(buckets.reduce((a, b) => a + avg[b].cleared, 0), buckets.reduce((a, b) => a + avg[b].prior, 0))}</TdNum>
            <TdNum />
          </tr>
          {pairs.map((q, i) => {
            const on = q === shown;
            const priorAll = q.comparable ? buckets.reduce((a, b) => a + q.rows[b].prior, 0) : 0;
            return (
              <tr key={q.toBbid} onClick={() => q.comparable && setSel(i)}
                  style={{ cursor: q.comparable ? "pointer" : "default", background: on ? B.cyanLight : "" }}>
                <Td style={{ fontWeight: on ? 600 : 400 }}>{fmtDate(q.fromDate)} → {fmtDate(q.toDate)}</Td>
                {q.comparable
                  ? <>
                      {rollCols.map(b => <TdNum key={b} title={`${fmt(q.rows[b].rolled)} of ${fmt(q.rows[b].prior)}`}>{pct(q.rows[b].rolled, q.rows[b].prior)}</TdNum>)}
                      <TdNum>{pct(buckets.reduce((a, b) => a + q.rows[b].cleared, 0), priorAll)}</TdNum>
                      <TdNum>{fmt(q.newAmt)}</TdNum>
                    </>
                  : <Td colSpan={rollCols.length + 2} style={{ ...sub, whiteSpace: "normal" }}>{q.reason}</Td>}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* The matrix for one month */}
      <div style={{ padding: "14px 12px 6px", fontSize: 12, fontWeight: 600 }}>
        {fmtDate(shown.fromDate)} → {fmtDate(shown.toDate)}
        <span style={{ ...sub, fontWeight: 400, marginLeft: 8 }}>rows: where the A/R sat at {fmtDate(shown.fromDate)} · columns: where the same dollars are at {fmtDate(shown.toDate)}</span>
      </div>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, whiteSpace: "nowrap" }}>
        <thead><tr>
          <th style={{ ...th, textAlign: "left" }}>From</th>
          <th style={{ ...th, textAlign: "right" }}>A/R held</th>
          <th style={{ ...th, textAlign: "right" }}>Invoices</th>
          <th style={{ ...th, textAlign: "right" }}>Paid</th>
          {buckets.map(b => <th key={b} style={{ ...th, textAlign: "right" }}>{labels[b]}</th>)}
        </tr></thead>
        <tbody>
          {buckets.map((b, i) => {
            const r = shown.rows[b];
            return (
              <tr key={b}>
                <Td style={{ fontWeight: 500 }}>{labels[b]}</Td>
                <TdNum>{fmt(r.prior)}</TdNum>
                <TdNum>{r.count}</TdNum>
                <TdNum style={{ color: GOOD }} title={fmt(r.cleared)}>{pct(r.cleared, r.prior)}</TdNum>
                {buckets.map((t, j) => {
                  const v = r.to[t];
                  const older = j > i;
                  return (
                    <TdNum key={t} title={fmt(v)}
                      style={{ color: v > 0 && older ? BAD : v > 0 ? "inherit" : "var(--color-text-tertiary, #bbb)",
                               fontWeight: j === i + 1 ? 600 : 400,
                               background: j === i + 1 && v > 0 ? "#FAECE7" : "" }}>
                      {r.prior > 0 && v > 0 ? pct(v, r.prior) : "·"}
                    </TdNum>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ ...sub, padding: "8px 12px 12px", lineHeight: 1.5 }}>
        Each row adds to 100% of what that bucket held: paid (gone, or the part of a partly-paid invoice that was paid),
        still in the same bucket, or rolled into an older one (red; the shaded cell is the next bucket — the classic
        step-down). New billings are invoices that were not on the earlier certificate. One certificate per month (the
        last one dated in it); the average is dollar-weighted across the months shown. Aging is by invoice date.
      </div>
    </Card>
  );
};
